import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as ordiniApi from '@/api/ordini'
import { ordiniLiveQueryOptions } from '@/config/queryClient'

const flattenErrorMessages = (value, keyPrefix = '') => {
  if (value == null) return []

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [keyPrefix ? `${keyPrefix}: ${String(value)}` : String(value)]
  }

  if (Array.isArray(value)) {
    return value.flatMap((entry) => flattenErrorMessages(entry, keyPrefix))
  }

  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nested]) => {
      const prefix = keyPrefix ? `${keyPrefix}.${key}` : key
      return flattenErrorMessages(nested, prefix)
    })
  }

  return []
}

const getApiErrorMessage = (error, fallbackMessage) => {
  const payload = error?.response?.data
  if (!payload) return fallbackMessage

  if (typeof payload === 'string') return payload

  if (typeof payload?.error === 'string') return payload.error

  if (payload?.error && typeof payload.error === 'object') {
    const nested = flattenErrorMessages(payload.error)
    if (nested.length > 0) return nested.join('\n')
  }

  const flat = flattenErrorMessages(payload)
  if (flat.length > 0) return flat.join('\n')

  return fallbackMessage
}

/**
 * Invalida cache che impattano disponibilità/riservate in UI.
 * Necessario perché il QueryClient ha staleTime globale a 5 minuti.
 */
const invalidateStockRelatedQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['giacenze-prodotti'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['giacenze-prodotti-sottoscorta'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['giacenze-componenti'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['giacenze-componenti-sottoscorta'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['giacenze-box'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['giacenza-prodotto'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['giacenza-componente'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['prodotti'], refetchType: 'all' })
  queryClient.invalidateQueries({ queryKey: ['tipologie-box'], refetchType: 'all' })
}

// ========== Ordini ==========

export const useOrdini = (params) => {
  return useQuery({
    queryKey: ['ordini', params],
    queryFn: () => ordiniApi.getOrdini(params).then(res => res.data),
    ...ordiniLiveQueryOptions,
  })
}

export const useOrdine = (id) => {
  return useQuery({
    queryKey: ['ordini', id],
    queryFn: () => ordiniApi.getOrdine(id).then(res => res.data),
    enabled: !!id,
    staleTime: 0, // Sempre considerato stale per avere dati freschi
    refetchOnMount: 'always', // Ri-fetch ogni volta che il componente monta
  })
}

export const useCreateOrdine = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.createOrdine,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Ordine creato con successo')
    },
    onError: (error) => {
      const errors = error.response?.data
      if (errors) {
        Object.entries(errors).forEach(([field, messages]) => {
          toast.error(`${field}: ${Array.isArray(messages) ? messages[0] : messages}`)
        })
      } else {
        toast.error('Errore durante la creazione dell\'ordine')
      }
    },
  })
}

export const useUpdateOrdine = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.updateOrdine(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Ordine aggiornato con successo')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante l\'aggiornamento'))
    },
  })
}

export const useDeleteOrdine = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.deleteOrdine,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Ordine eliminato con successo')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Errore durante l\'eliminazione'
      toast.error(errorMsg)
    },
  })
}

// ========== Actions Ordini ==========

export const useConfermaOrdine = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id) => ordiniApi.confermaOrdine(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Ordine confermato con successo')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante la conferma'))
    },
  })
}

export const useAnnullaOrdine = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, causale }) => ordiniApi.annullaOrdine(id, causale),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Ordine annullato')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || error.response?.data?.causale?.[0] || 'Errore durante l\'annullamento'
      toast.error(errorMsg)
    },
  })
}

export const useRipristinaOrdine = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id) => ordiniApi.ripristinaOrdine(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      queryClient.invalidateQueries({ queryKey: ['ordini-archiviati'] })
      queryClient.invalidateQueries({ queryKey: ['movimenti-magazzino'] })
      queryClient.invalidateQueries({ queryKey: ['movimenti-recenti'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Ordine ripristinato con successo')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante il ripristino'))
    },
  })
}

export const useCambiaStatoOrdine = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.cambiaStatoOrdine(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success(`Stato aggiornato a: ${response.data.stato_display}`)
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante il cambio stato'))
    },
  })
}

export const useSalvaComeBox = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ ordineId, codice, nome, configurazione_scatola }) => 
      ordiniApi.salvaComeBox(ordineId, { codice, nome, configurazione_scatola }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tipologie-box'] })
      const nomeBox = response.data?.tipologia_box?.nome || 'Box'
      toast.success(`${nomeBox} creata nel catalogo!`)
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Errore nel salvataggio della box'
      toast.error(errorMsg)
    },
  })
}

export const useStampaOrdine = (id) => {
  return useQuery({
    queryKey: ['ordini', id, 'stampa'],
    queryFn: () => ordiniApi.stampaOrdine(id).then(res => res.data),
    enabled: false, // Solo su richiesta
  })
}

export const useStatisticheOrdini = (params) => {
  return useQuery({
    queryKey: ['ordini', 'statistiche', params],
    queryFn: () => ordiniApi.getStatisticheOrdini(params).then(res => res.data),
  })
}

export const useStatiOrdine = () => {
  return useQuery({
    queryKey: ['ordini', 'stati'],
    queryFn: () => ordiniApi.getStatiOrdine().then(res => res.data),
    staleTime: Infinity, // Stati non cambiano
  })
}

// ========== Righe Ordine ==========

export const useAggiungiRiga = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ ordineId, data }) => ordiniApi.aggiungiRiga(ordineId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Riga aggiunta')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Errore durante l\'aggiunta della riga'
      toast.error(errorMsg)
    },
  })
}

export const useRimuoviRiga = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ ordineId, rigaId }) => ordiniApi.rimuoviRiga(ordineId, rigaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Riga rimossa')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Errore durante la rimozione della riga'
      toast.error(errorMsg)
    },
  })
}

// ========== Spedizioni ==========

export const useSpedizioni = (params) => {
  return useQuery({
    queryKey: ['spedizioni', params],
    queryFn: () => ordiniApi.getSpedizioni(params).then(res => res.data),
  })
}

export const useSpedizione = (id) => {
  return useQuery({
    queryKey: ['spedizioni', id],
    queryFn: () => ordiniApi.getSpedizione(id).then(res => res.data),
    enabled: !!id,
  })
}

export const useCreateSpedizione = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.createSpedizione,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Spedizione creata')
    },
    onError: () => {
      toast.error('Errore durante la creazione della spedizione')
    },
  })
}

export const useUpdateSpedizione = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.updateSpedizione(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Spedizione aggiornata')
    },
    onError: () => {
      toast.error('Errore durante l\'aggiornamento della spedizione')
    },
  })
}

export const useCambiaStatoSpedizione = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, stato }) => ordiniApi.cambiaStatoSpedizione(id, stato),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['spedizione', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Stato spedizione aggiornato')
    },
    onError: () => {
      toast.error('Errore durante il cambio stato spedizione')
    },
  })
}

export const useConfermaRientroSpedizione = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, causale }) => ordiniApi.confermaRientroSpedizione(id, causale),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['spedizione', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      invalidateStockRelatedQueries(queryClient)
      toast.success('Rientro fisico registrato; merce riservata per l’ordine')
    },
    onError: (error) => toast.error(error.response?.data?.error?.[0] || 'Errore nella conferma del rientro'),
  })
}

// ========== Pagamenti ==========

export const usePagamenti = (params) => {
  return useQuery({
    queryKey: ['pagamenti', params],
    queryFn: () => ordiniApi.getPagamenti(params).then(res => res.data),
  })
}

export const useCreatePagamento = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.createPagamento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pagamenti'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Pagamento registrato')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante la registrazione del pagamento'))
    },
  })
}

export const useUpdatePagamento = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.updatePagamento(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pagamenti'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Pagamento aggiornato')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante l\'aggiornamento del pagamento'))
    },
  })
}

export const useAnnullaPagamento = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.annullaPagamento(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pagamenti'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Pagamento annullato')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Errore durante l\'annullamento del pagamento'))
    },
  })
}

// ============================================================================
// FASE 7.4: Spedizioni Avanzate - Destinatari e Colli
// ============================================================================

// ========== Destinatari ==========

export const useDestinatari = (params) => {
  return useQuery({
    queryKey: ['destinatari', params],
    queryFn: () => ordiniApi.getDestinatari(params).then(res => res.data),
  })
}

export const useDestinatario = (id) => {
  return useQuery({
    queryKey: ['destinatari', id],
    queryFn: () => ordiniApi.getDestinatario(id).then(res => res.data),
    enabled: !!id,
  })
}

export const useDestinatariByIds = (ids = []) => {
  const uniqueIds = [...new Set(ids.filter(Boolean).map(Number))]
  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['destinatari', id],
      queryFn: () => ordiniApi.getDestinatario(id).then(res => res.data),
    })),
  })

  return new Map(
    queries
      .map((query) => query.data)
      .filter(Boolean)
      .map((destinatario) => [Number(destinatario.id), destinatario])
  )
}

export const useSearchDestinatari = (query) => {
  return useQuery({
    queryKey: ['destinatari', 'search', query],
    queryFn: () => ordiniApi.searchDestinatari(query).then(res => res.data),
    enabled: query?.length >= 2,
  })
}

export const useCreateDestinatario = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.createDestinatario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['destinatari'] })
      toast.success('Destinatario creato con successo')
    },
    onError: (error) => {
      const errors = error.response?.data
      if (errors) {
        const firstError = Object.values(errors)[0]
        toast.error(Array.isArray(firstError) ? firstError[0] : firstError)
      } else {
        toast.error('Errore durante la creazione del destinatario')
      }
    },
  })
}

export const useUpdateDestinatario = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.updateDestinatario(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['destinatari'] })
      toast.success('Destinatario aggiornato')
    },
    onError: () => {
      toast.error('Errore durante l\'aggiornamento del destinatario')
    },
  })
}

export const useDeleteDestinatario = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.deleteDestinatario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['destinatari'] })
      toast.success('Destinatario eliminato')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || 'Impossibile eliminare il destinatario (potrebbe essere in uso)'
      toast.error(errorMsg)
    },
  })
}

// ========== Colli ==========

export const useColli = (params) => {
  return useQuery({
    queryKey: ['colli', params],
    queryFn: () => ordiniApi.getColli(params).then(res => res.data),
  })
}

export const useCollo = (id) => {
  return useQuery({
    queryKey: ['colli', id],
    queryFn: () => ordiniApi.getCollo(id).then(res => res.data),
    enabled: !!id,
  })
}

export const useCreateCollo = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.createCollo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colli'] })
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Collo creato con successo')
    },
    onError: (error) => {
      const errors = error.response?.data
      if (errors) {
        const firstError = Object.values(errors)[0]
        toast.error(Array.isArray(firstError) ? firstError[0] : firstError)
      } else {
        toast.error('Errore durante la creazione del collo')
      }
    },
  })
}

export const useUpdateCollo = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }) => ordiniApi.updateCollo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colli'] })
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Collo aggiornato')
    },
    onError: () => {
      toast.error('Errore durante l\'aggiornamento del collo')
    },
  })
}

export const useDeleteCollo = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ordiniApi.deleteCollo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colli'] })
      queryClient.invalidateQueries({ queryKey: ['spedizioni'] })
      queryClient.invalidateQueries({ queryKey: ['ordini'] })
      toast.success('Collo eliminato')
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione del collo')
    },
  })
}
